import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, MapPin, Tv, Users, Loader2 } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { ChannelBadge } from '../components/ChannelBadge';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MatchDetailPage = () => {
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        const response = await axios.get(`${API}/matches/${id}`);
        setMatch(response.data);
      } catch (err) {
        console.error('Error fetching match:', err);
        setError('Jogo não encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchMatch();
  }, [id]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4" data-testid="loading-state">
        <Loader2 className="w-8 h-8 text-neon-green animate-spin" />
        <p className="text-zinc-400">Carregando...</p>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4" data-testid="error-state">
        <p className="text-zinc-400">{error || 'Jogo não encontrado'}</p>
        <Link to="/">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </Link>
      </div>
    );
  }

  const isLive = match.status === 'live';
  const isFinished = match.status === 'finished';

  return (
    <div className="space-y-6" data-testid="match-detail-page">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm uppercase tracking-wider">Voltar</span>
      </Link>

      {/* Match Header */}
      <div className="bg-card-dark border border-zinc-800 overflow-hidden">
        {/* League & Status */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-zinc-800 bg-pitch-black/50">
          <span className="text-sm font-medium text-zinc-400 uppercase tracking-wider">{match.league}</span>
          {isLive ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-live-red text-white text-sm font-bold uppercase tracking-wider animate-pulse-live">
              <span className="w-2 h-2 bg-white rounded-full"></span>
              AO VIVO
            </span>
          ) : isFinished ? (
            <span className="text-sm font-medium text-zinc-500 uppercase">Encerrado</span>
          ) : (
            <span className="text-sm font-medium text-neon-green uppercase">Em breve</span>
          )}
        </div>

        {/* Teams & Score */}
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center justify-between gap-4 md:gap-8">
            {/* Home Team */}
            <div className="flex-1 text-center">
              {match.team_home_logo && (
                <img 
                  src={match.team_home_logo} 
                  alt={match.team_home}
                  className="w-20 h-20 md:w-28 md:h-28 object-contain mx-auto mb-3"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <h2 className="font-heading text-xl md:text-3xl font-bold uppercase tracking-tight">{match.team_home}</h2>
            </div>

            {/* Score / VS */}
            <div className="flex-shrink-0 text-center">
              {isFinished || isLive ? (
                <div className="font-mono text-4xl md:text-6xl font-bold tracking-wider">
                  <span className={match.home_score > match.away_score ? 'text-neon-green' : 'text-white'}>
                    {match.home_score ?? 0}
                  </span>
                  <span className="text-zinc-600 mx-3">-</span>
                  <span className={match.away_score > match.home_score ? 'text-neon-green' : 'text-white'}>
                    {match.away_score ?? 0}
                  </span>
                </div>
              ) : (
                <span className="font-heading text-3xl md:text-5xl font-bold text-zinc-600">VS</span>
              )}
            </div>

            {/* Away Team */}
            <div className="flex-1 text-center">
              {match.team_away_logo && (
                <img 
                  src={match.team_away_logo} 
                  alt={match.team_away}
                  className="w-20 h-20 md:w-28 md:h-28 object-contain mx-auto mb-3"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <h2 className="font-heading text-xl md:text-3xl font-bold uppercase tracking-tight">{match.team_away}</h2>
            </div>
          </div>

          {/* Countdown */}
          {!isFinished && !isLive && (
            <div className="mt-8 pt-8 border-t border-zinc-800/50 flex justify-center">
              <CountdownTimer targetDate={match.match_date} targetTime={match.match_time} />
            </div>
          )}
        </div>

        {/* Match Info */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-800 border-t border-zinc-800">
          <div className="bg-card-dark px-4 py-4 text-center">
            <Clock className="w-5 h-5 text-neon-green mx-auto mb-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Horário</p>
            <p className="font-bold">{match.match_time}</p>
          </div>
          <div className="bg-card-dark px-4 py-4 text-center">
            <MapPin className="w-5 h-5 text-neon-green mx-auto mb-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Estádio</p>
            <p className="font-bold text-sm">{match.stadium}</p>
          </div>
          <div className="bg-card-dark px-4 py-4 text-center col-span-2 md:col-span-1">
            <Tv className="w-5 h-5 text-neon-green mx-auto mb-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Data</p>
            <p className="font-bold text-sm capitalize">{formatDate(match.match_date)}</p>
          </div>
        </div>
      </div>

      {/* Where to Watch */}
      <div className="bg-card-dark border border-zinc-800 p-4 md:p-6">
        <h3 className="font-heading text-xl font-bold uppercase tracking-tight flex items-center gap-2 mb-4">
          <Tv className="w-5 h-5 text-neon-green" />
          Onde Assistir
        </h3>
        {match.channels && match.channels.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {match.channels.map((channel, idx) => (
              <ChannelBadge key={idx} channel={channel} />
            ))}
          </div>
        ) : (
          <p className="text-zinc-500">Informação de transmissão a confirmar</p>
        )}
      </div>

      {/* Lineups */}
      {(match.home_lineup || match.away_lineup) && (
        <div className="bg-card-dark border border-zinc-800 p-4 md:p-6">
          <h3 className="font-heading text-xl font-bold uppercase tracking-tight flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-neon-green" />
            Prováveis Escalações
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-heading text-lg font-bold uppercase mb-2 text-neon-green">{match.team_home}</h4>
              <p className="text-zinc-300 text-sm leading-relaxed">{match.home_lineup || 'A confirmar'}</p>
            </div>
            <div>
              <h4 className="font-heading text-lg font-bold uppercase mb-2 text-neon-yellow">{match.team_away}</h4>
              <p className="text-zinc-300 text-sm leading-relaxed">{match.away_lineup || 'A confirmar'}</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MatchDetailPage;
